/**
 * หน้าต่างย่อยในเกม (ตัวละคร สกิล และที่จะมีต่อไป)
 *
 * เป็นงานฝั่งภาพล้วน ไม่รู้เรื่องกฎเกม ใครเรียกใช้เป็นคนเติมเนื้อหาเอง
 * แยกออกจาก main.ts เพราะเรื่องลากย้ายและการหนีบขอบจอไม่เกี่ยวกับตรรกะเกมเลย
 */

export interface GameWindow {
  /** ที่ใส่เนื้อหา ผู้เรียกเขียนลงตรงนี้ */
  readonly body: HTMLElement;
  open(): void;
  close(): void;
  /** คืนค่าสถานะหลังสลับ true = เปิดอยู่ */
  toggle(): boolean;
  isOpen(): boolean;
}

export interface WindowOpts {
  title: string;
  /** ตำแหน่งเริ่มต้นเป็นพิกเซลจากมุมซ้ายบน */
  x: number;
  y: number;
  /** เรียกทุกครั้งที่เปิด ใช้เติมเนื้อหาให้สด */
  onOpen?: () => void;
  /** เรียกทุกครั้งที่ปิด ใช้ล้างสถานะที่ไม่ควรค้างข้ามการเปิดครั้งหน้า */
  onClose?: () => void;
}

/** ตำแหน่งหน้าต่างที่ผู้เล่นลากไว้ (key = ชื่อหน้าต่าง) เก็บใน localStorage — พังได้ในโหมดส่วนตัว ใช้ค่าเริ่มต้นแทน */
const POS_KEY = "wickhaven.winpos";
function loadPos(): Record<string, [number, number]> {
  try { return JSON.parse(localStorage.getItem(POS_KEY) ?? "{}") ?? {}; } catch { return {}; }
}
function savePos(title: string, x: number, y: number): void {
  try {
    const all = loadPos();
    all[title] = [Math.round(x), Math.round(y)];
    localStorage.setItem(POS_KEY, JSON.stringify(all));
  } catch { /* โหมดส่วนตัว */ }
}

export function createWindow(host: HTMLElement, opts: WindowOpts): GameWindow {
  const el = document.createElement("div");
  el.className = "win";
  el.style.display = "none";
  const startX = typeof window !== "undefined" && window.innerWidth < 700 ? Math.min(opts.x, 10) : opts.x;
  const startY = typeof window !== "undefined" && window.innerHeight < 550 ? Math.min(opts.y, 40) : opts.y;
  // ตำแหน่งสุดท้ายที่ผู้เล่นลากไว้ชนะค่าเริ่มต้น (หนีบเข้าจอทุกครั้งที่เปิดอยู่แล้ว)
  const saved = typeof window !== "undefined" ? loadPos()[opts.title] : undefined;
  el.style.left = `${saved?.[0] ?? startX}px`;
  el.style.top = `${saved?.[1] ?? startY}px`;

  const header = document.createElement("header");
  const title = document.createElement("span");
  title.textContent = opts.title;
  const close = document.createElement("button");
  close.type = "button";
  close.textContent = "×";
  close.title = "ปิด";
  header.append(title, close);

  const body = document.createElement("div");
  body.className = "body";

  el.append(header, body);
  host.appendChild(el);

  /** หนีบให้หน้าต่างอยู่ในจอเสมอ เผื่อย่อหน้าต่างเบราว์เซอร์ลงหลังลากไปมุม */
  function clampIntoView(): void {
    const r = el.getBoundingClientRect();
    const maxX = Math.max(0, window.innerWidth - r.width);
    const maxY = Math.max(0, window.innerHeight - r.height);
    const curX = parseFloat(el.style.left) || 0;
    const curY = parseFloat(el.style.top) || 0;
    el.style.left = `${Math.min(Math.max(0, curX), maxX)}px`;
    el.style.top = `${Math.min(Math.max(0, curY), maxY)}px`;
  }

  // ---- ลากย้ายด้วยหัวหน้าต่าง ----
  let dragId = -1;
  let offX = 0;
  let offY = 0;

  header.addEventListener("pointerdown", (e) => {
    if (e.target === close) return;
    dragId = e.pointerId;
    const curX = parseFloat(el.style.left) || 0;
    const curY = parseFloat(el.style.top) || 0;
    offX = e.clientX - curX;
    offY = e.clientY - curY;
    try {
      header.setPointerCapture(e.pointerId);
    } catch {}
    e.preventDefault();
  });

  header.addEventListener("pointermove", (e) => {
    if (e.pointerId !== dragId) return;
    el.style.left = `${e.clientX - offX}px`;
    el.style.top = `${e.clientY - offY}px`;
    clampIntoView();
  });

  const endDrag = (e: PointerEvent): void => {
    if (e.pointerId === dragId) {
      dragId = -1;
      savePos(opts.title, parseFloat(el.style.left) || 0, parseFloat(el.style.top) || 0);
      try {
        header.releasePointerCapture(e.pointerId);
      } catch {}
    }
  };
  header.addEventListener("pointerup", endDrag);
  header.addEventListener("pointercancel", endDrag);

  const api: GameWindow = {
    body,
    isOpen: () => el.style.display !== "none",
    open() {
      el.style.display = "block";
      opts.onOpen?.();
      clampIntoView();
      requestAnimationFrame(() => clampIntoView());
    },
    close() {
      if (el.style.display === "none") return;
      el.style.display = "none";
      opts.onClose?.();
    },
    toggle() {
      if (api.isOpen()) api.close();
      else api.open();
      return api.isOpen();
    },
  };

  close.addEventListener("click", () => api.close());
  window.addEventListener("resize", () => {
    if (api.isOpen()) clampIntoView();
  });

  return api;
}
